import useSWR from 'swr'
import {
  ArrowRight,
  CalendarDays,
  Clock3,
  Database,
  Loader2,
  UserRound,
  Wrench,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  formatActionTime,
  formatBusinessDate,
  formatEvidence,
  formatEvidenceValue,
  getActionLabel,
  getCoverageState,
  getEvidenceLevelLabel,
  getFieldLabel,
  getPlatformChangesRequest,
  getPlatformChangesPayload,
  getSourceLabel,
  isPlatformChangeSupported,
} from '@/features/roas/notes/platform-changes'

const PLATFORM_LABELS = {
  google_ads: 'Google Ads',
  meta_ads: 'Meta Ads',
}

const COVERAGE_DESCRIPTIONS = {
  available: '该 Campaign 当天所属账号的采集窗口完整，事件数量可视为完整结果。',
  partial: '当天仅有部分时间段完成采集，可能还有尚未覆盖的事件。',
  event_matched: '已找到与 Campaign 和日期直接匹配的事件，但暂时无法证明全天覆盖完整。',
  not_collected: '当天没有匹配事件，也没有可验证的账号采集记录。',
  unavailable: '平台调整服务当前不可用，请稍后重试。',
  unsupported: '该 Note 的投放渠道暂不支持平台调整查询。',
  unknown: '暂时无法判断该 Campaign 的采集状态。',
}

const coverageBadgeVariant = (status) => {
  if (status === 'available' || status === 'event_matched') return 'success'
  if (status === 'partial' || status === 'not_collected') return 'warning'
  if (status === 'unavailable') return 'danger'
  return 'default'
}

const EventMetaItem = ({ icon: Icon, label, value }) => (
  <div className="flex min-w-0 items-start gap-2">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="truncate text-sm font-medium">{value}</p>
    </div>
  </div>
)

const EvidenceItem = ({ item, index }) => {
  const fieldLabel = getFieldLabel(item.fieldPath)
  const oldValue = formatEvidenceValue(item, 'old')
  const newValue = formatEvidenceValue(item, 'new')

  return (
    <div className="rounded-lg border bg-background p-3">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium">{fieldLabel}</p>
        <Badge size="sm">
          {getEvidenceLevelLabel(item.evidenceLevel)}
        </Badge>
      </div>
      <div className="grid items-stretch gap-2 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
        <div className="rounded-md bg-muted/60 p-3">
          <p className="text-xs text-muted-foreground">调整前</p>
          <p className="mt-1 break-words text-base font-semibold">{oldValue}</p>
        </div>
        <div className="flex items-center justify-center">
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3">
          <p className="text-xs text-emerald-700">调整后</p>
          <p className="mt-1 break-words text-base font-semibold text-emerald-800">{newValue}</p>
        </div>
      </div>
      <details className="mt-3 text-sm">
        <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
          查看技术证据
        </summary>
        <div className="mt-2 space-y-3 rounded-md bg-muted/40 p-3">
          <p className="break-all text-xs text-muted-foreground">
            资源：{item.resourceType || '未知'} · {item.resourceId || `记录 ${index + 1}`}
            {item.fieldPath ? ` · ${item.fieldPath}` : ''}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <p className="text-xs text-muted-foreground">原始调整前</p>
              <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded bg-background p-2 text-xs">
                {formatEvidence(item.oldValue)}
              </pre>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">原始调整后</p>
              <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded bg-background p-2 text-xs">
                {formatEvidence(item.newValue)}
              </pre>
            </div>
          </div>
        </div>
      </details>
    </div>
  )
}

const EventDetails = ({ event, index }) => {
  const platform = PLATFORM_LABELS[event.platform] || event.platform || '未知平台'
  const occurredAt = formatActionTime(
    event.actionAtShanghai || event.occurredAt || event.time || event.createdAt,
  )
  const actionTypes = event.canonicalActionTypes?.length
    ? event.canonicalActionTypes
    : [event.action || event.actionType || event.type]
  const operator = event.actorNames?.join('、') || event.operator || event.operatorName || event.user || '未知'
  const sources = event.sourceTypes?.length
    ? event.sourceTypes.map(getSourceLabel).join('、')
    : getSourceLabel(event.source)
  const evidence = Array.isArray(event.evidence) && event.evidence.length > 0
    ? event.evidence
    : [{
        resourceType: event.resourceType,
        resourceId: event.resourceId,
        fieldPath: event.fieldPath,
        oldValue: event.oldEvidence ?? event.oldValue ?? event.before,
        newValue: event.newEvidence ?? event.newValue ?? event.after,
        oldValueNormalized: event.oldValueNormalized,
        newValueNormalized: event.newValueNormalized,
        valueUnit: event.valueUnit,
        currencyCode: event.currencyCode,
        evidenceLevel: event.evidenceLevel,
      }]

  return (
    <li className="overflow-hidden rounded-xl border bg-card shadow-sm">
      <div className="border-b bg-muted/30 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-medium text-muted-foreground">事件 {index + 1}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {actionTypes.filter(Boolean).map((actionType) => (
                <Badge key={actionType} variant="primary" size="sm">
                  {getActionLabel(actionType)}
                </Badge>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm font-medium">
            <Clock3 className="h-4 w-4 text-muted-foreground" />
            {occurredAt}
          </div>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <EventMetaItem icon={Database} label="投放平台" value={platform} />
          <EventMetaItem icon={UserRound} label="操作人" value={operator} />
          <EventMetaItem icon={Wrench} label="操作来源" value={sources} />
        </div>
      </div>
      <div className="space-y-3 p-4">
        <p className="text-sm font-medium">调整内容</p>
        {evidence.map((item, evidenceIndex) => (
          <EvidenceItem
            key={`${item.resourceId || evidenceIndex}-${item.fieldPath || ''}`}
            item={item}
            index={evidenceIndex}
          />
        ))}
      </div>
    </li>
  )
}

const PlatformChangesDialog = ({ note, open, onOpenChange }) => {
  const isSupported = isPlatformChangeSupported(note?.mediaSource)
  const request = getPlatformChangesRequest(note)
  const key = open && isSupported ? request : null
  const { data, error, isLoading } = useSWR(key)
  const {
    coverage,
    events,
    total,
    campaignName,
    date,
  } = getPlatformChangesPayload(data)
  const coverageState = getCoverageState(coverage)
  const platform = events[0]?.platform
    ? PLATFORM_LABELS[events[0].platform] || events[0].platform
    : note?.mediaSource?.startsWith('meta') ? 'Meta Ads' : 'Google Ads'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>平台调整详情</DialogTitle>
          <DialogDescription className="break-words">
            {campaignName || note?.campaignName || '未知 Campaign'}
          </DialogDescription>
        </DialogHeader>
        {!isSupported ? (
          <p className="text-sm text-muted-foreground">该投放渠道暂不支持平台调整查询。</p>
        ) : isLoading ? (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            正在加载平台调整记录…
          </div>
        ) : error ? (
          <p className="py-8 text-sm text-destructive">平台调整记录加载失败，请稍后重试。</p>
        ) : (
          <div className="space-y-5">
            <div className="grid gap-3 rounded-xl border bg-muted/20 p-4 sm:grid-cols-4">
              <EventMetaItem
                icon={CalendarDays}
                label="Note 日期"
                value={formatBusinessDate(date || note?.date)}
              />
              <EventMetaItem icon={Database} label="投放平台" value={platform} />
              <div>
                <p className="text-xs text-muted-foreground">事件数量</p>
                <p className="mt-0.5 text-2xl font-semibold">{total}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">采集状态</p>
                <Badge
                  className="mt-1"
                  variant={coverageBadgeVariant(coverageState.status)}
                  size="sm"
                >
                  {coverageState.label}
                </Badge>
              </div>
            </div>

            <div className="rounded-lg border-l-4 border-l-emerald-500 bg-emerald-50/40 px-4 py-3 text-sm">
              <p className="font-medium">状态说明</p>
              <p className="mt-1 text-muted-foreground">
                {coverage?.message || COVERAGE_DESCRIPTIONS[coverageState.status]}
              </p>
            </div>

            {note?.reason && (
              <div className="rounded-lg border p-4">
                <p className="text-sm font-medium">Note 记录</p>
                <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{note.reason}</p>
              </div>
            )}

            {events.length === 0 ? (
              <div className="rounded-lg border border-dashed py-10 text-center text-sm text-muted-foreground">
                当天没有找到平台调整事件。
              </div>
            ) : (
              <ul className="space-y-4">
                {events.map((event, index) => (
                  <EventDetails
                    key={event.rawEventId || event.id || index}
                    event={event}
                    index={index}
                  />
                ))}
              </ul>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default PlatformChangesDialog
